import { SKILL_IDS, SKILL_LABEL } from '../domain/skills.js';
import { gerarInstanciaAtividade } from './activityGenerator.js';
import { normalizarHabilidadeId, normalizarHabilidadesLista } from './skillAliases.js';
import { normalizeSessionResultItem } from './session-results.js';

export const SESSION_PLAN_STATUS = Object.freeze({
  PROPOSTO: 'proposto', AJUSTADO: 'ajustado', APROVADO: 'aprovado', APLICADO: 'aplicado', REGISTRADO: 'registrado', REJEITADO: 'rejeitado',
});

export const RESULT_SCORE = Object.freeze({ conseguiu: 1, parcial: 0.5, nao_conseguiu: 0, 'não conseguiu': 0 });

export const DEFAULT_PLANNER_CONFIG = Object.freeze({
  duracaoSessaoMin: 45,
  minItens: 2,
  maxItens: 4,
  deficitThreshold: 60,
  pesos: { meta: 0.45, barreira: 0.2, evidencia: 0.15, deficit: 0.1, recencia: 0.1 },
  janelaRotacao: 2,
  intervaloManutencaoDias: [7, 30],
  mastery: { sessoesConsecutivas: 3, subirNivel: 0.8, dominio: 0.9, simplificar: 0.5, simplificarSessoes: 2 },
});

const TRANSICOES_VALIDAS = Object.freeze({
  proposto: ['ajustado', 'aprovado', 'rejeitado'],
  ajustado: ['aprovado', 'rejeitado'],
  aprovado: ['aplicado', 'rejeitado'],
  aplicado: ['registrado'],
  registrado: [],
  rejeitado: ['proposto'],
});

export function gerarPlanoSessao(ctx = {}) {
  const config = mergeConfig(DEFAULT_PLANNER_CONFIG, ctx.config || {});
  const aluno = ctx.aluno || {};
  const banco = normalizarBanco(ctx.bancoAtividades || ctx.banco || []);
  const historicoLogs = ctx.historicoLogs || ctx.logs || [];
  const metasPEI = normalizarMetasPEI(ctx.metasPEI || ctx.pei?.metas || []);
  const avaliacao = ctx.avaliacaoMaisRecente || ctx.avaliacao || ctx.assessment || {};
  const dataPlano = ctx.data || new Date().toISOString().slice(0, 10);
  const alunoId = getId(aluno);
  const caseStudy = ctx.caseStudy || {};

  const habilidadesRankeadas = rankearHabilidades({
    deficits: extrairDeficits(avaliacao, config),
    metasAtivas: metasPEI.filter(isMetaAtiva),
    recencia: calcularRecencia(historicoLogs),
    pesos: config.pesos,
    caseStudy,
    logs: historicoLogs,
  });

  const necessidadesAluno = normalizarNecessidades(aluno.necessidades || aluno.necessidadesEducacionais || aluno.deficiencias || aluno.necessidade);
  const candidatas = habilidadesRankeadas.flatMap((habilidadeRank) => banco
    .filter((atividade) => atividadeParaHabilidade(atividade, habilidadeRank.id))
    .filter((atividade) => prerequisitosAtendidos(atividade, historicoLogs, alunoId, config))
    .map((atividade) => {
      const selecao = aplicarRegrasSelecao(atividade, { historicoLogs, resultados: historicoLogs, alunoId, config });
      const perfil = scoreAtividade(atividade, { aluno, habilidadeRank, necessidadesAluno, caseStudy });
      return { atividade, habilidade: habilidadeRank.id, score: perfil.score + habilidadeRank.score + selecao.bonus - selecao.penalidade, motivo: montarMotivo(atividade, habilidadeRank, selecao, perfil), contextoRecomendacao: montarContextoRecomendacao(habilidadeRank, selecao, perfil) };
    }));

  const itens = montarSequencia(candidatas, { resultados: historicoLogs, alunoId, metasPEI, config }).map((item) => {
    const atividade = banco.find((candidate) => candidate.id === item.atividadeId);
    if (!atividade) return item;
    const instancia = gerarInstanciaAtividade(atividade, { aluno, historicoInstancias: historicoLogs, resultadoRecente: ultimoResultado(historicoLogs, atividade.id, alunoId), semente: `${alunoId || 'aluno'}:${atividade.id}:${dataPlano}` });
    return { ...item, instancia, comboKey: instancia.comboKey, tituloSnapshot: instancia.titulo || item.tituloSnapshot };
  });

  return { ...montarSessionPlan({ aluno, itens, metasPEI, profissionalId: ctx.profissionalId }), data: dataPlano };
}

export function extrairDeficits(avaliacao = {}, config = DEFAULT_PLANNER_CONFIG) {
  const threshold = config.deficitThreshold ?? DEFAULT_PLANNER_CONFIG.deficitThreshold;
  const origem = avaliacao.habilidades || avaliacao.pontuacoes || avaliacao.scores || avaliacao.resultados || avaliacao;
  const deficits = {};
  for (const habilidade of SKILL_IDS) {
    const valor = getNumericValue(origem?.[habilidade]);
    if (Number.isFinite(valor) && valor < threshold) deficits[habilidade] = valor;
  }
  for (const [chave, valorBruto] of Object.entries(origem || {})) {
    const id = normalizarHabilidadeId(chave);
    const valor = getNumericValue(valorBruto);
    if (Number.isFinite(valor) && valor < threshold) deficits[id] = valor;
  }
  return deficits;
}

export function rankearHabilidades({ deficits = {}, metasAtivas = [], recencia = {}, pesos = DEFAULT_PLANNER_CONFIG.pesos, caseStudy = {}, logs = [] }) {
  const deficitsNorm = Object.fromEntries(Object.entries(deficits).map(([id, value]) => [normalizarHabilidadeId(id), value]));
  const recenciaNorm = Object.fromEntries(Object.entries(recencia).map(([id, value]) => [normalizarHabilidadeId(id), value]));
  const ids = new Set([...Object.keys(deficitsNorm), ...Object.keys(recenciaNorm)]);
  for (const meta of metasAtivas) for (const hab of habilidadesDaMeta(meta)) ids.add(hab);
  return [...ids].map((id) => {
    const metaDetalhe = metasAtivas.find((meta) => habilidadesDaMeta(meta).includes(id)) || null;
    const meta = metaDetalhe ? 1 : 0;
    const barreira = metaDetalhe && textoBarreira(metaDetalhe, caseStudy) ? 1 : 0;
    const evidenciaRecente = evidenciaRecenteDaHabilidade(id, logs);
    const evidencia = evidenciaRecente?.prioridade ?? 0;
    const deficit = Number.isFinite(deficitsNorm[id]) ? (100 - deficitsNorm[id]) / 100 : 0;
    const rec = Math.min((recenciaNorm[id] || 0) / 6, 1);
    const score = meta * (pesos.meta ?? 0)
      + barreira * (pesos.barreira ?? 0)
      + evidencia * (pesos.evidencia ?? 0)
      + deficit * (pesos.deficit ?? 0)
      + rec * (pesos.recencia ?? 0);
    return {
      id,
      score,
      deficit: deficitsNorm[id],
      metaAtiva: Boolean(meta),
      metaDetalhe,
      barreiraPrioritaria: Boolean(barreira),
      barreira: textoBarreira(metaDetalhe, caseStudy),
      evidenciaRecente,
      sessoesDesdeUltima: recenciaNorm[id] ?? null,
    };
  }).filter((item) => item.score > 0 && (
    item.metaAtiva
    || item.barreiraPrioritaria
    || item.evidenciaRecente
    || Number.isFinite(item.deficit)
  )).sort((a, b) =>
    Number(b.metaAtiva) - Number(a.metaAtiva)
    || Number(b.barreiraPrioritaria) - Number(a.barreiraPrioritaria)
    || b.score - a.score
  );
}

export function rankearAtividadesPorAvaliacao({ avaliacao = {}, aluno = {}, bancoAtividades = [], logs = [], metasPEI = [], caseStudy = {}, filtros = {}, topN = 8, config = DEFAULT_PLANNER_CONFIG } = {}) {
  const banco = normalizarBanco(bancoAtividades);
  const ranking = rankearHabilidades({
    deficits: extrairDeficits(avaliacao, config),
    metasAtivas: normalizarMetasPEI(metasPEI).filter(isMetaAtiva),
    recencia: calcularRecencia(logs),
    pesos: config.pesos,
    caseStudy,
    logs,
  });
  const necessidadesAluno = normalizarNecessidades(aluno.necessidades || aluno.necessidadesEducacionais || aluno.deficiencias || aluno.necessidade);
  const alunoId = getId(aluno);
  const candidatas = ranking.flatMap((habilidadeRank) => banco
    .filter((atividade) => atividadeParaHabilidade(atividade, habilidadeRank.id))
    .filter((atividade) => atendeFiltros(atividade, filtros))
    .map((atividade) => {
      const selecao = aplicarRegrasSelecao(atividade, { historicoLogs: logs, resultados: logs, alunoId, config });
      const perfil = scoreAtividade(atividade, { aluno, habilidadeRank, necessidadesAluno, caseStudy });
      const score = Math.round((perfil.score + habilidadeRank.score + selecao.bonus - selecao.penalidade) * 100);
      return {
        ...atividade,
        score,
        habilidades_deficit: [habilidadeRank.id],
        motivo_recomendacao: montarMotivo(atividade, habilidadeRank, selecao, perfil),
        contexto_recomendacao: montarContextoRecomendacao(habilidadeRank, selecao, perfil),
      };
    }));
  const unicas = new Map();
  for (const item of candidatas.sort((a, b) => b.score - a.score || a.titulo.localeCompare(b.titulo, 'pt-BR'))) if (!unicas.has(item.id)) unicas.set(item.id, item);
  return [...unicas.values()].slice(0, topN);
}

export function calcularRecencia(logs = []) {
  const recencia = {};
  const logsOrdenados = [...logs].sort((a, b) => new Date(b.data || b.createdAt || 0) - new Date(a.data || a.createdAt || 0));
  const vistas = new Set();
  logsOrdenados.forEach((log, index) => {
    for (const habilidade of normalizarLista(log.habilidades || log.habilidades_alvo || log.habilidade)) if (!vistas.has(habilidade)) { recencia[habilidade] = index; vistas.add(habilidade); }
  });
  for (const habilidade of SKILL_IDS) if (logsOrdenados.length && !vistas.has(habilidade)) recencia[habilidade] = logsOrdenados.length;
  return recencia;
}

export function aplicarRegrasSelecao(atividade, { historicoLogs = [], resultados = [], alunoId, config = DEFAULT_PLANNER_CONFIG } = {}) {
  const atividadeId = atividade.id;
  const logsDoAluno = historicoLogs.filter((log) => pertenceAoAluno(log, alunoId));
  const posicaoUltima = logsDoAluno.findIndex((log) => getAtividadeId(log) === atividadeId || (log.atividadeIds || []).includes(atividadeId));
  const sessoesDesdeUltima = posicaoUltima < 0 ? Infinity : Math.max(0, posicaoUltima);
  const penalidade = Number.isFinite(sessoesDesdeUltima) ? Math.max(0, 1 - sessoesDesdeUltima / Math.max(config.janelaRotacao, 1)) : 0;
  const mastery = calcularMastery(atividadeId, resultados, alunoId, config);
  const bonus = mastery.dominio && deveRevisarManutencao(mastery.ultimoRegistro, config) ? 0.25 : 0;
  return { penalidade, bonus, mastery, sessoesDesdeUltima };
}

export function calcularMastery(atividadeId, resultados = [], alunoId, config = DEFAULT_PLANNER_CONFIG) {
  const registros = resultados.filter((registro) => pertenceAoAluno(registro, alunoId)).flatMap((registro) => normalizarResultadosRegistro(registro)).filter((item) => item.atividadeId === atividadeId).sort((a, b) => new Date(b.data || 0) - new Date(a.data || 0));
  const consecutivos = registros.slice(0, config.mastery.sessoesConsecutivas);
  const media = consecutivos.length ? consecutivos.reduce((sum, item) => sum + item.score, 0) / consecutivos.length : null;
  const duasUltimas = registros.slice(0, config.mastery.simplificarSessoes);
  const dominioNaAtividade = consecutivos.length >= config.mastery.sessoesConsecutivas && media >= config.mastery.dominio;
  const contextosGeneralizacao = [...new Set(registros.flatMap((item) => item.generalizacao || []))];
  return {
    registros,
    media,
    consecutivos: consecutivos.length,
    dominio: dominioNaAtividade,
    dominioNaAtividade,
    generalizado: contextosGeneralizacao.length > 0,
    dominioConsolidado: dominioNaAtividade && contextosGeneralizacao.length > 0,
    contextosGeneralizacao,
    subirNivel: consecutivos.length >= config.mastery.sessoesConsecutivas && media >= config.mastery.subirNivel,
    simplificar: duasUltimas.length >= config.mastery.simplificarSessoes && duasUltimas.every((item) => item.score < config.mastery.simplificar),
    ultimoRegistro: registros[0] || null,
  };
}

export function decidirNivel(atividade, resultados = [], alunoId, config = DEFAULT_PLANNER_CONFIG) {
  const nivelAtual = Number(atividade.nivel_dificuldade || atividade.nivel || 1);
  const mastery = calcularMastery(atividade.id, resultados, alunoId, config);
  if (mastery.simplificar) return Math.max(1, nivelAtual - 1);
  if (mastery.subirNivel && !mastery.dominio) return nivelAtual + 1;
  return nivelAtual;
}

export function montarSequencia(candidatas = [], { resultados = [], alunoId, metasPEI = [], config = DEFAULT_PLANNER_CONFIG } = {}) {
  const unicas = [];
  const usados = new Set();
  for (const candidata of [...candidatas].sort((a, b) => b.score - a.score)) {
    if (!candidata.atividade?.id || usados.has(candidata.atividade.id)) continue;
    usados.add(candidata.atividade.id);
    unicas.push(candidata);
    if (unicas.length >= config.maxItens) break;
  }
  const selecionadas = unicas.slice(0, Math.max(config.minItens, Math.min(config.maxItens, unicas.length)));
  const tipos = ['aquecimento', 'foco', 'foco', 'encerramento'];
  let duracaoUsada = 0;
  return selecionadas.map((candidata, index) => {
    const atividade = candidata.atividade;
    const duracao = Number(atividade.duracao_estimada_min || atividade.duracaoMin || Math.round(config.duracaoSessaoMin / Math.max(selecionadas.length, 1)));
    duracaoUsada += duracao;
    return { ordem: index + 1, atividadeId: atividade.id, tituloSnapshot: atividade.titulo || atividade.nome || atividade.id, habilidadesAlvo: normalizarLista(atividade.habilidades_alvo || atividade.habilidades || candidata.habilidade), nivelSugerido: decidirNivel(atividade, resultados, alunoId, config), duracaoMin: duracao, tipo: tipos[index] || 'foco', motivo: candidata.motivo, contextoRecomendacao: candidata.contextoRecomendacao || null, statusItem: 'proposto', metasPEIAlvo: metasRelacionadas(candidata.habilidade, metasPEI) };
  }).map((item, _index, lista) => {
    if (duracaoUsada <= config.duracaoSessaoMin || item.tipo !== 'foco') return item;
    const excesso = duracaoUsada - config.duracaoSessaoMin;
    const focos = lista.filter((it) => it.tipo === 'foco').length || 1;
    return { ...item, duracaoMin: Math.max(5, item.duracaoMin - Math.ceil(excesso / focos)) };
  });
}

export function montarSessionPlan({ aluno = {}, itens = [], metasPEI = [], profissionalId = null }) {
  const alunoId = getId(aluno);
  const data = new Date().toISOString().slice(0, 10);
  const habilidades = [...new Set(itens.flatMap((item) => item.habilidadesAlvo || []))];
  return { id: `plan_${data}_${alunoId || 'aluno'}`, alunoId, profissionalId, data, status: SESSION_PLAN_STATUS.PROPOSTO, geradoEm: new Date().toISOString(), geradoPor: 'motor_v18', duracaoTotalMin: itens.reduce((sum, item) => sum + Number(item.duracaoMin || 0), 0), objetivoSessao: montarObjetivoSessao(habilidades), metasPEIAlvo: metasPEI.filter((meta) => habilidadesDaMeta(meta).some((hab) => habilidades.includes(hab))).map((meta) => meta.id || meta.codigo || meta.titulo).filter(Boolean), itens, resultado: null };
}

export function transicionarPlano(plano, novoStatus, extra = {}) {
  const atual = plano?.status;
  const validas = TRANSICOES_VALIDAS[atual] || [];
  if (!validas.includes(novoStatus)) throw new Error(`Transição inválida de ${atual} para ${novoStatus}.`);
  return { ...plano, ...extra, status: novoStatus, atualizadoEm: new Date().toISOString() };
}

export function aplicarResultadoPlano(plano, resultado) {
  if (plano.status !== SESSION_PLAN_STATUS.APLICADO) throw new Error('Resultado só pode ser registrado depois que o plano estiver aplicado.');
  const itens = (resultado.itens || []).map((item) => {
    const normalizado = normalizeSessionResultItem(item);
    return {
      ...normalizado,
      score: Number.isFinite(normalizado.score) ? normalizado.score : RESULT_SCORE[normalizado.desempenho] ?? 0,
    };
  });
  return { ...plano, status: SESSION_PLAN_STATUS.REGISTRADO, resultado: { registradoEm: new Date().toISOString(), itens, observacaoGeral: resultado.observacaoGeral || '' }, atualizadoEm: new Date().toISOString() };
}

export function atualizarResultadoPlano(plano, resultado) {
  if (plano.status !== SESSION_PLAN_STATUS.REGISTRADO) throw new Error('Somente resultados já registrados podem ser atualizados.');
  const itens = (resultado.itens || []).map((item) => {
    const normalizado = normalizeSessionResultItem(item);
    return {
      ...normalizado,
      score: Number.isFinite(normalizado.score) ? normalizado.score : RESULT_SCORE[normalizado.desempenho] ?? 0,
    };
  });
  return {
    ...plano,
    resultado: {
      registradoEm: plano.resultado?.registradoEm || new Date().toISOString(),
      atualizadoEm: new Date().toISOString(),
      itens,
      observacaoGeral: resultado.observacaoGeral || '',
    },
    atualizadoEm: new Date().toISOString(),
  };
}

export function normalizarMetasPEI(peisOuMetas = []) {
  return (peisOuMetas || []).flatMap((item) => Array.isArray(item?.metas)
    ? item.metas.map((meta) => ({ ...meta, peiId: item.id, studentId: item.studentId || item.alunoId, habilidades_alvo: normalizarLista(meta.habilidades_alvo || meta.habilidade_alvo || meta.habilidades || meta.habilidade) }))
    : { ...item, habilidades_alvo: normalizarLista(item.habilidades_alvo || item.habilidade_alvo || item.habilidades || item.habilidade) });
}

function normalizarBanco(banco) { const lista = Array.isArray(banco) ? banco : Array.isArray(banco.atividades) ? banco.atividades : Array.isArray(banco.items) ? banco.items : []; return lista.map((atividade) => ({ ...atividade, habilidades_alvo: normalizarLista(atividade.habilidades_alvo || atividade.habilidades || atividade.habilidade), prerequisitos: normalizarLista(atividade.prerequisitos) })); }
function atividadeParaHabilidade(atividade, habilidade) { return normalizarLista(atividade.habilidades_alvo || atividade.habilidades || atividade.habilidade).includes(normalizarHabilidadeId(habilidade)); }
// Antes era um filtro rígido: sem interseção com necessidadesAluno, a atividade sumia da
// lista. Como o cadastro do aluno é texto livre e o banco de atividades acumulou grafias
// diferentes ao longo de 3 gerações (ex.: "TDAH" vs "tdah", "surdez" vs "surdez_libras"),
// um filtro rígido podia zerar a lista inteira por divergência de vocabulário. Agora é um
// bônus/penalidade leve: atividade com necessidade compatível sobe no ranking, sem
// desaparecer quando o vocabulário simplesmente não bate.
function necessidadeScore(atividade, necessidadesAluno = []) {
  const indicadas = normalizarNecessidades(atividade.necessidades_indicadas || atividade.necessidades || atividade.publico);
  if (!necessidadesAluno.length || !indicadas.length) return 0;
  return indicadas.some((necessidade) => necessidadesAluno.includes(necessidade)) ? 0.03 : 0;
}
function atendeFiltros(atividade, filtros = {}) { if (filtros.eixo && atividade.eixo !== filtros.eixo) return false; if (filtros.habilidade && !atividadeParaHabilidade(atividade, filtros.habilidade)) return false; if (filtros.dificuldade && Number(atividade.nivel_dificuldade || 0) !== Number(filtros.dificuldade)) return false; if (filtros.necessidade && !normalizarNecessidades(atividade.necessidades_indicadas || []).includes(normalizeNeed(filtros.necessidade))) return false; if (filtros.busca) { const busca = normalizeNeed(filtros.busca); const texto = normalizeNeed(`${atividade.titulo || ''} ${atividade.objetivo || ''} ${(atividade.funcoes || []).join(' ')} ${(atividade.habilidades_alvo || []).join(' ')}`); if (!texto.includes(busca)) return false; } if (filtros.funcao && !normalizarNecessidades(atividade.funcoes || []).includes(normalizeNeed(filtros.funcao))) return false; return true; }
function prerequisitosAtendidos(atividade, resultados, alunoId, config) { const prerequisitos = normalizarLista(atividade.prerequisitos); return !prerequisitos.length || prerequisitos.every((atividadeId) => calcularMastery(atividadeId, resultados, alunoId, config).dominio); }
function scoreAtividade(atividade, { aluno, habilidadeRank, necessidadesAluno = [], caseStudy = {} }) {
  const idade = getIdade(aluno);
  const faixa = atividade.faixa_etaria || {};
  let score = 1 - Math.max(0, Number(atividade.nivel_dificuldade || 1) - 1) * 0.03;
  if (Number.isFinite(idade) && Number.isFinite(faixa.min) && idade < faixa.min) score -= Math.min(0.08, (faixa.min - idade) * 0.02);
  if (Number.isFinite(idade) && Number.isFinite(faixa.max) && idade > faixa.max) score -= Math.min(0.08, (idade - faixa.max) * 0.02);
  score += necessidadeScore(atividade, necessidadesAluno);

  const textoAtividade = normalizeNeed(JSON.stringify({
    titulo: atividade.titulo,
    objetivo: atividade.objetivo || atividade.objetivo_operacional,
    contexto: atividade.contexto,
    formato: atividade.formato,
    materiais: atividade.materiais,
    variacoes: atividade.variacoes,
    adaptacoes: atividade.adaptacoes || atividade.adaptacoes_por_perfil,
  }));
  const interesses = normalizarTermos(aluno.interesses || aluno.interests);
  const interesse = interesses.find((termo) => termo.length >= 3 && textoAtividade.includes(termo)) || '';
  if (interesse) score += 0.12;

  const acessibilidade = normalizarTermos([
    aluno.nivelComunicacao,
    aluno.communicationLevel,
    aluno.perfilSensorial,
    aluno.sensoryProfile,
    caseStudy?.blocks?.communication?.summary,
  ]);
  const recursoAcessivel = acessibilidade.find((termo) => termo.length >= 3 && textoAtividade.includes(termo)) || '';
  if (recursoAcessivel) score += 0.12;

  return { score, interesse, recursoAcessivel, habilidadeRank };
}

function montarMotivo(_atividade, habilidadeRank, selecao, perfil = {}) {
  const habilidade = SKILL_LABEL[habilidadeRank.id] || String(habilidadeRank.id || '').replaceAll('_', ' ');
  const partes = [];
  const meta = habilidadeRank.metaDetalhe;
  if (meta) partes.push(`trabalha a meta ativa de ${habilidade.toLowerCase()}`);
  else if (Number.isFinite(habilidadeRank.deficit)) partes.push(`trabalha ${habilidade.toLowerCase()}, ainda em desenvolvimento na avaliação`);
  else partes.push(`trabalha ${habilidade.toLowerCase()}`);

  if (habilidadeRank.barreira) partes.push(`está ligada à barreira “${resumir(habilidadeRank.barreira, 90)}”`);
  const evidencia = habilidadeRank.evidenciaRecente;
  if (evidencia?.texto) partes.push(evidencia.texto);
  if (perfil.recursoAcessivel) partes.push('há recurso compatível com a comunicação ou acessibilidade registrada');
  else if (perfil.interesse) partes.push(`dialoga com o interesse “${perfil.interesse.replaceAll('_', ' ')}”`);
  if (Number.isFinite(selecao.sessoesDesdeUltima) && selecao.sessoesDesdeUltima > 0) partes.push(`não foi repetida nas últimas ${selecao.sessoesDesdeUltima} sessões`);

  return `Indicada porque ${partes.slice(0, 3).join('; ')}.`;
}

function montarContextoRecomendacao(habilidadeRank, selecao, perfil = {}) {
  return {
    habilidadeId: habilidadeRank.id,
    metaId: habilidadeRank.metaDetalhe?.id || null,
    barreira: habilidadeRank.barreira || '',
    evidenciaRecente: habilidadeRank.evidenciaRecente || null,
    interesse: perfil.interesse || '',
    recursoAcessivel: perfil.recursoAcessivel || '',
    sessoesDesdeUltima: Number.isFinite(selecao.sessoesDesdeUltima) ? selecao.sessoesDesdeUltima : null,
  };
}

function textoBarreira(meta = {}, caseStudy = {}) {
  return String(meta?.barreira || meta?.barrier || caseStudy?.blocks?.barriers?.summary || '').trim();
}

function evidenciaRecenteDaHabilidade(habilidadeId, logs = []) {
  const alvo = normalizarHabilidadeId(habilidadeId);
  const registros = [...(logs || [])]
    .sort((a, b) => String(b.data || b.date || b.createdAt || '').localeCompare(String(a.data || a.date || a.createdAt || '')))
    .flatMap((log) => {
      const habilidadesLog = normalizarLista(log.habilidades || log.habilidades_alvo || log.habilidade);
      const itens = Array.isArray(log.itens) ? log.itens : [];
      if (!itens.length && habilidadesLog.includes(alvo)) return [{ ...log, data: log.data || log.date }];
      return itens
        .filter((item) => {
          const habilidadesItem = normalizarLista(item.habilidades || item.habilidadesAlvo || item.habilidade);
          return habilidadesItem.includes(alvo) || (!habilidadesItem.length && habilidadesLog.includes(alvo));
        })
        .map((item) => ({ ...item, data: log.data || log.date || log.createdAt }));
    });
  const recente = registros[0];
  if (!recente) return null;
  const desempenho = String(recente.desempenho || recente.resultado || '').toLowerCase();
  const scoreBruto = Number(recente.score);
  const score = Number.isFinite(scoreBruto) ? (scoreBruto <= 1 ? scoreBruto : scoreBruto / 100) : RESULT_SCORE[desempenho];
  const apoio = String(recente.apoio || recente.nivelApoio || '').toLowerCase();
  const pesoApoio = { ajuda_intensa: 1, ajuda_moderada: 0.75, pista_leve: 0.4, independente: 0 }[apoio] ?? 0;
  const prioridade = Math.max(0, Math.min(1, ((Number.isFinite(score) ? 1 - score : 0) + pesoApoio) / 2));
  const desempenhoTexto = { conseguiu: 'conseguiu', parcial: 'ficou parcial', nao_conseguiu: 'ainda não foi demonstrada', 'não conseguiu': 'ainda não foi demonstrada' }[desempenho] || '';
  const apoioTexto = { ajuda_intensa: 'ajuda intensa', ajuda_moderada: 'ajuda moderada', pista_leve: 'pista leve', independente: 'independência' }[apoio] || '';
  const texto = desempenhoTexto && apoioTexto
    ? `no último atendimento, a resposta ${desempenhoTexto} com ${apoioTexto}`
    : desempenhoTexto ? `no último atendimento, a resposta ${desempenhoTexto}` : '';
  return { data: recente.data || '', desempenho, apoio: apoio || null, prioridade, texto };
}

function normalizarTermos(valor) {
  const lista = Array.isArray(valor) ? valor.flat(Infinity) : [valor];
  return [...new Set(lista.flatMap((item) => normalizeNeed(item).split('_')).filter(Boolean))];
}

function resumir(valor, limite = 90) {
  const texto = String(valor || '').trim();
  return texto.length > limite ? `${texto.slice(0, limite - 1).trim()}…` : texto;
}
function montarObjetivoSessao(habilidades) { return habilidades.length ? `Trabalhar ${habilidades.slice(0, 3).join(', ')} com atividades sequenciadas e registro de resultado.` : 'Realizar atendimento com atividades ajustadas ao perfil do aluno.'; }
function normalizarResultadosRegistro(registro) {
  if (Array.isArray(registro.resultado?.itens)) return registro.resultado.itens.map((item) => ({
    atividadeId: item.atividadeId,
    score: Number.isFinite(item.score) ? item.score : RESULT_SCORE[item.desempenho] ?? 0,
    generalizacao: normalizeSessionResultItem(item).generalizacao,
    data: registro.resultado.registradoEm || registro.data || registro.createdAt,
  }));
  if (Array.isArray(registro.itens)) return registro.itens.map((item) => ({
    atividadeId: item.atividadeId || item.id,
    score: Number.isFinite(item.score) ? item.score : RESULT_SCORE[item.desempenho || item.resultado] ?? 0,
    generalizacao: normalizeSessionResultItem(item).generalizacao,
    data: registro.data || registro.createdAt,
  }));
  return [{
    atividadeId: getAtividadeId(registro),
    score: Number.isFinite(registro.score) ? registro.score : RESULT_SCORE[registro.desempenho || registro.resultado] ?? 0,
    generalizacao: normalizeSessionResultItem(registro).generalizacao,
    data: registro.data || registro.createdAt,
  }].filter((item) => item.atividadeId);
}
function deveRevisarManutencao(ultimoRegistro, config) { if (!ultimoRegistro?.data) return false; const dias = Math.floor((Date.now() - new Date(ultimoRegistro.data).getTime()) / 86400000); return config.intervaloManutencaoDias.some((intervalo) => Math.abs(dias - intervalo) <= 1); }
function metasRelacionadas(habilidade, metasPEI = []) { return metasPEI.filter((meta) => habilidadesDaMeta(meta).includes(normalizarHabilidadeId(habilidade))).map((meta) => meta.id || meta.codigo || meta.titulo).filter(Boolean); }
function habilidadesDaMeta(meta = {}) { return normalizarLista(meta.habilidades || meta.habilidades_alvo || meta.habilidade_alvo || meta.habilidadeId || meta.habilidade); }
function isMetaAtiva(meta = {}) { return !meta.status || ['ativa', 'em_andamento', 'em andamento', 'proposta'].includes(String(meta.status).toLowerCase()); }
function pertenceAoAluno(registro, alunoId) { return !alunoId || [registro.alunoId, registro.studentId, registro.aluno_id].filter(Boolean).includes(alunoId); }
function getAtividadeId(registro) { return registro.atividadeId || registro.activityId || registro.atividade_id || registro.idAtividade; }
function getNumericValue(valor) { if (typeof valor === 'number') return valor; if (valor && typeof valor === 'object') return getNumericValue(valor.score ?? valor.valor ?? valor.nota ?? valor.percentual); const parsed = Number(valor); return Number.isFinite(parsed) ? parsed : NaN; }
function getId(obj = {}) { return obj.id || obj.alunoId || obj.studentId || obj.uid || null; }
function getIdade(aluno = {}) { if (Number.isFinite(aluno.idade)) return aluno.idade; const nascimentoValor = aluno.dataNascimento || aluno.nascimento; if (!nascimentoValor) return NaN; return Math.floor((Date.now() - new Date(nascimentoValor).getTime()) / 31557600000); }
function normalizarLista(valor) { return normalizarHabilidadesLista(valor); }
function normalizarNecessidades(valor) { if (!valor) return []; const lista = Array.isArray(valor) ? valor : [valor]; return lista.filter(Boolean).map(normalizeNeed); }
function normalizeNeed(value = '') { return String(value).normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, ''); }
function ultimoResultado(logs = [], atividadeId, alunoId) { const registro = logs.filter((log) => pertenceAoAluno(log, alunoId)).flatMap(normalizarResultadosRegistro).find((item) => item.atividadeId === atividadeId); if (!registro) return null; if (registro.score >= 0.85) return 'facil'; if (registro.score < 0.5) return 'dificil'; return 'ok'; }
function mergeConfig(base, override) { return { ...base, ...override, pesos: { ...base.pesos, ...(override.pesos || {}) }, mastery: { ...base.mastery, ...(override.mastery || {}) } }; }
